import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';

import { Box, Button, ButtonGroup, Card, Flex, FlexColumn, Select, Spinner, Tag, Text, Tooltip } from 'core/components';
import api from 'core/util/api';
import { formatDateTime, fromNow, protoTimestampToIsoString } from 'core/util/dateUtils';
import { InfoCard } from 'app/views/infrastructure/components/InfoCard';
import InfoCardValue from 'app/views/infrastructure/components/InfoCardValue';
import DeviceConfigView from 'app/components/device/DeviceConfigView';
import { FiCopy, FiDownload, FiRefreshCcw, FiSearch, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import CopyToClipboardButton from 'app/components/CopyToClipboardButton';
import { InputGroup, Popover, Position } from '@blueprintjs/core';

const CodeMirrorContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  background-color: ${({ theme }) => (theme.name === 'dark' ? '#1e1e1e' : '#263238')};
  border-radius: 4px;
  overflow: hidden;

  > div {
    flex: 1;
    display: flex;
  }

  .cm-editor {
    flex: 1;
    height: 100%;
    width: 100%;
    background-color: transparent;
  }

  .cm-scroller {
    flex: 1;
    overflow: auto;
  }

  .cm-gutters {
    background-color: transparent;
    color: ${({ theme }) => (theme.name === 'dark' ? '#6e6e6e' : '#537f7e')};
    border: none;
  }
`;

const SearchMatchBadge = styled.div`
  display: flex;
  align-items: center;
  padding: 2px 8px;
  margin-right: 4px;
  border-radius: 3px;
  font-size: 12px;
  color: #a7b6c2;
  font-weight: 500;
  white-space: nowrap;
`;

const SearchPopover = styled.div`
  padding: 8px;
  min-width: 340px;
  display: flex;
  gap: 4px;
  align-items: center;
`;

const SearchInputWrapper = styled.div`
  flex: 1;
`;

const NavigationButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

function mapRevisionsToOptions(revisions) {
  return revisions
    .map((revision) => {
      const id = revision?.id ?? revision?.revision?.id;
      const timestamp = revision?.first_fetched ?? revision?.revision?.first_fetched;

      const capturedAtIso = protoTimestampToIsoString(timestamp);
      const absoluteDate = capturedAtIso ? formatDateTime(capturedAtIso, 'YYYY-MM-DD HH:mm:ss z') : null;
      const absoluteLabel = `Revision ${id}${absoluteDate ? `: ${absoluteDate}` : ''}`;
      const relativeAge = capturedAtIso ? fromNow(capturedAtIso) : null;

      return id
        ? {
            id,
            absoluteLabel,
            capturedAtIso,
            relativeAge,
            sortValue: capturedAtIso ? Date.parse(capturedAtIso) : 0
          }
        : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.sortValue - a.sortValue)
    .map(({ id, absoluteLabel, relativeAge }, index) => {
      let label = absoluteLabel;

      if (relativeAge) {
        label = (
          <Flex alignItems="center" gap={1}>
            {index === 0 && <Tag intent="primary">Latest</Tag>}
            <Text fontWeight="medium">{label}</Text>
            <Text small muted>
              ({relativeAge})
            </Text>
          </Flex>
        );
      } else if (index === 0) {
        label = (
          <Text>
            {label}{' '}
            <Text small muted>
              (Latest)
            </Text>
          </Text>
        );
      }

      return { label, value: id };
    });
}

@inject('$app', '$auth')
@withRouter
@observer
export default class DeviceConfigs extends Component {
  static propTypes = {
    device: PropTypes.object,
    match: PropTypes.object,
    history: PropTypes.object,
    // Standalone mode props (optional)
    standalone: PropTypes.bool,
    initialRevisionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onRevisionChange: PropTypes.func
  };

  static defaultProps = {
    device: null,
    match: null,
    history: null,
    standalone: false,
    initialRevisionId: undefined,
    onRevisionChange: undefined
  };

  deviceConfigViewRef = React.createRef();

  state = {
    revisionOptions: [],
    currentRevision: null,
    configData: '',
    diffData: '',
    commitDetails: null,
    isLoadingRevisions: false,
    isLoadingContent: false,
    revisionsError: null,
    revisionContentError: null,
    selectedMode: 'config',
    selectedRevisionId: undefined,
    searchMatchInfo: { totalMatches: 0, currentMatchIndex: -1 },
    isSearchPopoverOpen: false
  };

  isUnmounted = false;

  activeRevisionRequest = null;

  // Derive current revision id from route params or state (for standalone mode)
  getCurrentRevisionId = () => {
    const { match, standalone } = this.props;
    const { revisionOptions, selectedRevisionId } = this.state;

    // In standalone mode, use internal state
    if (standalone) {
      return selectedRevisionId;
    }

    // In routed mode, derive from URL params
    const isConfigsTab = match?.params?.tab === 'configs';
    if (!isConfigsTab || !revisionOptions.length) {
      return undefined;
    }
    const paramId = match?.params?.index;
    if (paramId) {
      const opt = revisionOptions.find((o) => `${o.value}` === `${paramId}`);
      if (opt) {
        return opt.value;
      }
      return undefined;
    }
    return revisionOptions[0]?.value;
  };

  componentDidMount() {
    this.loadRevisions();
  }

  componentDidUpdate(prevProps) {
    const { device, match, standalone, initialRevisionId } = this.props;
    const { match: prevMatch, device: previousDevice, initialRevisionId: prevInitialRevisionId } = prevProps;
    const currentDeviceId = device?.id;
    const previousDeviceId = previousDevice?.id;

    if (currentDeviceId && currentDeviceId !== previousDeviceId) {
      this.loadRevisions();
    }

    // Handle standalone mode - load initial revision if provided
    if (standalone && initialRevisionId && initialRevisionId !== prevInitialRevisionId) {
      const { revisionOptions } = this.state;
      const matched = revisionOptions.find((o) => `${o.value}` === `${initialRevisionId}`);
      if (matched) {
        this.setState(
          {
            selectedRevisionId: matched.value,
            configData: '',
            diffData: '',
            commitDetails: null,
            revisionContentError: null,
            currentRevision: null
          },
          () => this.loadRevisionContent(matched.value)
        );
      }
      return;
    }

    // Skip route-based updates in standalone mode
    if (standalone) {
      return;
    }

    const isConfigsTab = match?.params?.tab === 'configs';
    const wasConfigsTab = prevMatch?.params?.tab === 'configs';

    if (isConfigsTab) {
      const urlRevision = match?.params?.index;
      const prevUrlRevision = wasConfigsTab ? prevMatch?.params?.index : undefined;
      const { selectedRevisionId, revisionOptions, isLoadingRevisions } = this.state;

      if (
        !isLoadingRevisions &&
        urlRevision &&
        urlRevision !== prevUrlRevision &&
        `${selectedRevisionId}` !== `${urlRevision}`
      ) {
        const matched = revisionOptions.find((o) => `${o.value}` === `${urlRevision}`);
        if (matched) {
          this.setState(
            {
              selectedRevisionId: matched.value,
              configData: '',
              diffData: '',
              commitDetails: null,
              revisionContentError: null,
              currentRevision: null
            },
            () => this.loadRevisionContent(matched.value)
          );
        } else if (urlRevision !== prevUrlRevision) {
          this.setState({ revisionContentError: 'Revision not found.', selectedRevisionId: undefined });
        }
      }
    } else if (wasConfigsTab) {
      this.setState({
        selectedRevisionId: undefined,
        configData: '',
        diffData: '',
        commitDetails: null,
        currentRevision: null
      });
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  get deviceId() {
    const { device } = this.props;
    return device?.id;
  }

  loadRevisions = async (options = {}) => {
    const { isRefresh = false } = options;
    const { deviceId } = this;
    const { match, standalone, initialRevisionId } = this.props;
    const isConfigsTab = standalone || match?.params?.tab === 'configs';

    let revisionParam;
    if (standalone) {
      revisionParam = initialRevisionId;
    } else if (isConfigsTab) {
      revisionParam = match?.params?.index;
    }

    if (!deviceId) {
      return;
    }

    // Store current state before refresh
    const { revisionOptions, configData, diffData, selectedRevisionId, commitDetails, currentRevision } = this.state;
    const wasViewingLatest = isRefresh && revisionOptions[0]?.value === this.getCurrentRevisionId();
    const currentRevisionId = isRefresh ? this.getCurrentRevisionId() : undefined;

    this.setState({
      isLoadingRevisions: true,
      revisionsError: null,
      revisionOptions: isRefresh ? revisionOptions : [],
      configData: isRefresh ? configData : '',
      diffData: isRefresh ? diffData : '',
      revisionContentError: null,
      selectedRevisionId: isRefresh ? selectedRevisionId : undefined,
      commitDetails: isRefresh ? commitDetails : null,
      currentRevision: isRefresh ? currentRevision : null
    });

    try {
      const response = await api.get(`/api/ui/devices/${deviceId}/configs`);
      if (this.isUnmounted) {
        return;
      }

      const newRevisionOptions = mapRevisionsToOptions(response?.revisions ?? []);
      let revisionContentError = null;
      if (revisionParam) {
        const exists = newRevisionOptions.some((o) => `${o.value}` === `${revisionParam}`);
        if (!exists) {
          revisionContentError = 'Revision not found.';
        }
      }

      this.setState(
        {
          revisionOptions: newRevisionOptions,
          isLoadingRevisions: false,
          revisionContentError
        },
        () => {
          // Handle refresh: if user was viewing latest and a new one is available, switch to it
          if (isRefresh) {
            const newLatestRevision = newRevisionOptions[0]?.value;
            if (wasViewingLatest && newLatestRevision && newLatestRevision !== currentRevisionId) {
              // User was viewing latest, and there's a newer revision available
              if (standalone) {
                this.setState({ selectedRevisionId: newLatestRevision }, () => {
                  this.loadRevisionContent(newLatestRevision);
                });
              } else {
                // Update URL to new latest revision
                this.handleRevisionChange(newLatestRevision);
              }
            } else if (currentRevisionId) {
              // Reload the current revision to get fresh data
              // This isn't strictly necessary because revisions are immutable.
              this.loadRevisionContent(currentRevisionId);
            }
          } else if (standalone) {
            // Initial load logic for standalone mode
            const targetRevision = revisionParam || newRevisionOptions[0]?.value;
            if (targetRevision && !revisionContentError) {
              this.setState({ selectedRevisionId: targetRevision }, () => {
                this.loadRevisionContent(targetRevision);
              });
            }
          } else {
            // Initial load logic for routed mode
            const rev = this.getCurrentRevisionId();
            if (rev && !revisionContentError) {
              this.loadRevisionContent(rev);
            }
          }
        }
      );
    } catch (error) {
      if (this.isUnmounted) {
        return;
      }

      const message = typeof error === 'string' ? error : error?.message || 'Unable to load revisions.';
      this.setState({
        isLoadingRevisions: false,
        revisionsError: message
      });
    }
  };

  loadRevisionContent = async (revisionId) => {
    const { deviceId } = this;

    if (!deviceId || !revisionId) {
      this.setState({ configData: '', diffData: '', commitDetails: null });
      return;
    }

    this.activeRevisionRequest = revisionId;

    this.setState({
      isLoadingContent: true,
      revisionContentError: null
    });

    try {
      const response = await api.get(`/api/ui/devices/${deviceId}/configs/${revisionId}`);

      if (this.isUnmounted || this.activeRevisionRequest !== revisionId) {
        return;
      }

      const config = response?.config ?? {};
      const encoding = config.encoding ?? config.configEncoding;
      const configData = config.configData ?? config.config_data ?? '';
      const diffData = config.diffData ?? config.diff_data ?? '';
      const commitDetails = config.commitDetails ?? config.commit_details ?? null;

      const isPlainEncoding =
        encoding == null ||
        encoding === 'CONFIG_ENCODING_UNSPECIFIED' ||
        encoding === 'CONFIG_ENCODING_PLAIN' ||
        encoding === 0 ||
        encoding === 1;

      if (!isPlainEncoding) {
        this.setState({
          configData: '',
          diffData: '',
          commitDetails: null,
          isLoadingContent: false,
          revisionContentError: `Unsupported encoding ${encoding}`,
          currentRevision: null
        });
        return;
      }

      const safeConfigData = typeof configData === 'string' ? configData : '';
      const safeDiffData = typeof diffData === 'string' ? diffData : '';
      let normalizedCommitDetails = null;

      if (commitDetails) {
        const commitTime = commitDetails.time ? protoTimestampToIsoString(commitDetails.time) : null;
        normalizedCommitDetails =
          (commitTime === null || commitTime === '0001-01-01T00:00:00.000Z') &&
          !commitDetails.user &&
          !commitDetails.comment
            ? null
            : {
                ...commitDetails,
                time: commitTime ? formatDateTime(commitTime, 'YYYY-MM-DD HH:mm:ss z') : null
              };
      }

      // Format first_fetched and last_fetched timestamps
      const revision = config?.revision ?? null;
      let normalizedRevision = null;
      if (revision) {
        const firstFetchedIso = revision.first_fetched ? protoTimestampToIsoString(revision.first_fetched) : null;
        const lastFetchedIso = revision.last_fetched ? protoTimestampToIsoString(revision.last_fetched) : null;
        normalizedRevision = {
          ...revision,
          first_fetched: firstFetchedIso ? formatDateTime(firstFetchedIso, 'YYYY-MM-DD HH:mm:ss z') : null,
          last_fetched: lastFetchedIso ? formatDateTime(lastFetchedIso, 'YYYY-MM-DD HH:mm:ss z') : null,
          last_fetched_iso: lastFetchedIso // Store ISO for relative time calculation
        };
      }

      this.setState({
        configData: safeConfigData,
        diffData: safeDiffData,
        commitDetails: normalizedCommitDetails,
        isLoadingContent: false,
        revisionContentError: null,
        selectedRevisionId: revisionId,
        currentRevision: normalizedRevision
      });
    } catch (error) {
      if (this.isUnmounted || this.activeRevisionRequest !== revisionId) {
        return;
      }

      const message = typeof error === 'string' ? error : error?.message || 'Unable to load configuration.';
      this.setState({
        configData: '',
        diffData: '',
        commitDetails: null,
        isLoadingContent: false,
        revisionContentError: message,
        selectedRevisionId: revisionId,
        currentRevision: null
      });
    } finally {
      if (this.activeRevisionRequest === revisionId) {
        this.activeRevisionRequest = null;
      }
    }
  };

  handleRevisionChange = (value) => {
    const revisionId = Array.isArray(value) ? value[0] : value;
    const { match, history, standalone, onRevisionChange } = this.props;

    // Clear current content while loading new revision
    this.setState({
      configData: '',
      diffData: '',
      commitDetails: null,
      revisionContentError: null,
      selectedRevisionId: revisionId || undefined,
      currentRevision: null
    });

    if (revisionId) {
      // In standalone mode, just update state and load content
      if (standalone) {
        this.loadRevisionContent(revisionId);
        // Call optional callback
        if (onRevisionChange) {
          onRevisionChange(revisionId);
        }
      } else {
        // In routed mode, update URL
        const isConfigsTab = match?.params?.tab === 'configs';
        if (isConfigsTab) {
          history.push(this.buildConfigsRevisionPath(revisionId));
        }
        // Optimistically load without waiting for URL change re-render
        this.loadRevisionContent(revisionId);
      }
    } else if (!standalone) {
      const isConfigsTab = match?.params?.tab === 'configs';
      if (isConfigsTab) {
        history.push(this.buildConfigsRevisionPath());
      }
    }
  };

  handleModeChange = (mode) => {
    const { configData, diffData } = this.state;

    // If switching to diff mode but no diff data available, show message
    if (mode === 'diff' && (!diffData || diffData === configData)) {
      this.setState({
        selectedMode: mode,
        revisionContentError: 'Diff data is not available for this revision.'
      });
      return;
    }

    this.setState({
      selectedMode: mode,
      revisionContentError: null
    });
  };

  handleRefresh = () => {
    this.loadRevisions({ isRefresh: true });
  };

  handleSearch = (e) => {
    this.setState((state) => ({
      ...state,
      searchQuery: e.target.value ?? ''
    }));
  };

  handleSearchPopoverToggle = () => {
    this.setState((state) => ({
      isSearchPopoverOpen: !state.isSearchPopoverOpen
    }));
  };

  handleSearchPopoverClose = () => {
    this.setState({ isSearchPopoverOpen: false });
  };

  handleSearchMatchChange = (matchInfo) => {
    this.setState({ searchMatchInfo: matchInfo });
  };

  handleFindNextMatch = () => {
    if (this.deviceConfigViewRef.current?.findNextMatch) {
      const matchInfo = this.deviceConfigViewRef.current.findNextMatch();
      if (matchInfo) {
        this.setState({ searchMatchInfo: matchInfo });
      }
    }
  };

  handleFindPreviousMatch = () => {
    if (this.deviceConfigViewRef.current?.findPreviousMatch) {
      const matchInfo = this.deviceConfigViewRef.current.findPreviousMatch();
      if (matchInfo) {
        this.setState({ searchMatchInfo: matchInfo });
      }
    }
  };

  handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) {
        // Shift+Enter goes to previous match
        this.handleFindPreviousMatch();
      } else {
        // Enter goes to next match
        this.handleFindNextMatch();
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      this.handleFindNextMatch();
    }
  };

  handleDownload = () => {
    const { configData, diffData, selectedMode, currentRevision } = this.state;
    const { device } = this.props;
    const deviceName = device ? device.get('device_name') : 'unknown_device';

    const isDiff = selectedMode === 'diff';
    const dataToDownload = isDiff ? diffData : configData;
    const zeroPaddedId = String(currentRevision.id).padStart(3, '0');
    const currentRevisionFirstFetched = (currentRevision.first_fetched || '').replaceAll(' ', '_');

    const fileName = `${deviceName}--${isDiff ? 'diff--' : ''}revision_${zeroPaddedId}--${currentRevisionFirstFetched || 'unknown_date'}.txt`;

    const blob = new Blob([dataToDownload], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  renderContentBody() {
    const {
      isLoadingRevisions,
      isLoadingContent,
      revisionContentError,
      revisionsError,
      revisionOptions,
      configData,
      diffData,
      selectedMode,
      searchQuery = ''
    } = this.state;
    const { device } = this.props;

    const currentRevisionId = this.getCurrentRevisionId();

    if (isLoadingRevisions || isLoadingContent) {
      return (
        <Flex flex={1} alignItems="center" justifyContent="center">
          <Spinner />
        </Flex>
      );
    }

    if (revisionsError) {
      return (
        <Flex flex={1} alignItems="center" justifyContent="center" px={3} textAlign="center">
          <Text color="danger">Failed to load configuration revisions. {revisionsError}</Text>
        </Flex>
      );
    }

    if (!revisionOptions.length) {
      return (
        <Flex flex={1} alignItems="center" justifyContent="center" px={3} textAlign="center">
          <Text muted>No captured configurations are available for this device.</Text>
        </Flex>
      );
    }

    if (revisionContentError) {
      return (
        <Flex flex={1} alignItems="center" justifyContent="center" px={3} textAlign="center">
          <Text color="danger">{revisionContentError}</Text>
        </Flex>
      );
    }

    if (!currentRevisionId) {
      return (
        <Flex flex={1} alignItems="center" justifyContent="center" px={3} textAlign="center">
          <Text muted>Select a configuration revision to view its contents.</Text>
        </Flex>
      );
    }

    return (
      <CodeMirrorContainer>
        <DeviceConfigView
          ref={this.deviceConfigViewRef}
          searchQuery={searchQuery}
          onSearchMatchChange={this.handleSearchMatchChange}
          configText={selectedMode === 'diff' ? diffData : configData}
          syntax={selectedMode === 'diff' ? 'diff' : device?.vendor || ''}
        />
      </CodeMirrorContainer>
    );
  }

  render() {
    const { device } = this.props;
    const {
      revisionOptions,
      isLoadingContent,
      isLoadingRevisions,
      revisionsError,
      revisionContentError,
      configData,
      diffData,
      selectedMode,
      commitDetails,
      currentRevision,
      searchQuery,
      isSearchPopoverOpen,
      searchMatchInfo
    } = this.state;
    const currentRevisionId = this.getCurrentRevisionId();

    if (!device) {
      return (
        <Flex height="100%" alignItems="center" justifyContent="center">
          <Text muted>No device selected.</Text>
        </Flex>
      );
    }

    let placeholder = 'No revisions available';

    if (revisionsError) {
      placeholder = 'Unable to load revisions';
    } else if (isLoadingRevisions) {
      placeholder = 'Loading revisions...';
    } else if (revisionOptions.length) {
      placeholder = 'Select a revision';
    }

    const contentBody = this.renderContentBody();

    return (
      <FlexColumn gap={2} height="100%" overflow="hidden" p="4px">
        <Flex gap={2} alignItems="center" width="100%">
          <ButtonGroup>
            <Button active={selectedMode === 'config'} onClick={() => this.handleModeChange('config')} width={60}>
              Config
            </Button>
            <Button active={selectedMode === 'diff'} onClick={() => this.handleModeChange('diff')} width={60}>
              Diff
            </Button>
          </ButtonGroup>
          <Box flex={1}>
            <Select
              fill
              showFilter
              loading={isLoadingRevisions}
              disabled={!revisionOptions.length || !!revisionsError}
              options={revisionOptions}
              values={currentRevisionId || undefined}
              placeholder={placeholder}
              onChange={this.handleRevisionChange}
            />
          </Box>
          <ButtonGroup>
            <Popover
              isOpen={isSearchPopoverOpen}
              onClose={this.handleSearchPopoverClose}
              position={Position.BOTTOM_RIGHT}
              content={
                <SearchPopover>
                  <SearchInputWrapper>
                    <InputGroup
                      autoFocus
                      leftIcon="search"
                      placeholder="Find in config..."
                      value={searchQuery}
                      onChange={this.handleSearch}
                      onKeyDown={this.handleSearchKeyDown}
                      type="search"
                      rightElement={this.renderSearchMatchBadge()}
                    />
                  </SearchInputWrapper>
                  <NavigationButtons>
                    <Button
                      small
                      minimal
                      intent="secondary"
                      icon={FiChevronUp}
                      onClick={this.handleFindPreviousMatch}
                      disabled={!searchQuery || searchMatchInfo.totalMatches === 0}
                      title="Previous match (Shift+Enter)"
                    />
                    <Button
                      small
                      minimal
                      intent="secondary"
                      icon={FiChevronDown}
                      onClick={this.handleFindNextMatch}
                      disabled={!searchQuery || searchMatchInfo.totalMatches === 0}
                      title="Next match (Enter)"
                    />
                  </NavigationButtons>
                </SearchPopover>
              }
            >
              <Button
                icon={FiSearch}
                text="Find"
                active={isSearchPopoverOpen || !!searchQuery}
                onClick={this.handleSearchPopoverToggle}
              />
            </Popover>
            <CopyToClipboardButton
              text={selectedMode === 'diff' ? diffData : configData}
              disabled={!contentBody || isLoadingContent || !!revisionContentError || (!configData && !diffData)}
              intent="none"
            >
              <Button icon={FiCopy} text="Copy" />
            </CopyToClipboardButton>
            <Button icon={FiDownload} text="Download" onClick={this.handleDownload} disabled={isLoadingRevisions} />
            <Button icon={FiRefreshCcw} text="Reload" onClick={this.handleRefresh} loading={isLoadingRevisions} />
          </ButtonGroup>
          <Tooltip
            content={
              <Flex flexDirection="column">
                <Text as="h4">SSH Access Requirements</Text>
                <p>
                  Kentik brings context to your observability data by correlating flow records with device
                  configurations. To do this securely, we use SSH to read configuration data from the device.
                </p>
                <Text as="h4">Our Access Policy:</Text>
                <ul
                  style={{
                    paddingInlineStart: '20px'
                  }}
                >
                  <li>
                    <strong>Read-Only:</strong> We do not request, nor do we want, write access to your
                    infrastructure.{' '}
                  </li>
                  <li>
                    <strong>Zero Configuration Changes:</strong> Our features are designed to scrape config diffs and
                    execute ad-hoc troubleshooting commands (e.g., via the AI Assistant). We will never execute
                    configure terminal or commit changes.
                  </li>
                </ul>
                <Text as="h4">Recommended Configuration:</Text>
                <p>
                  Please provision a dedicated SSH user with restricted permissions (e.g., Cisco Privilege Level 1-14,
                  depending on your specific show command authorization requirements).
                </p>
              </Flex>
            }
          >
            <Button small as="a" type="button">
              Learn More
            </Button>
          </Tooltip>
        </Flex>
        <Flex gap={2} flex="0 auto">
          <InfoCard flex="1">
            <div className="label">Revision ID</div>
            <InfoCardValue>{currentRevisionId}</InfoCardValue>
            <div className="label">First Fetched</div>
            <InfoCardValue>{currentRevision?.first_fetched || 'unknown'}</InfoCardValue>
            <div className="label">Last Fetched</div>
            <InfoCardValue>
              {currentRevision?.last_fetched ? (
                <>
                  {currentRevision.last_fetched}
                  {currentRevision.last_fetched_iso && (
                    <Text small muted>
                      {' '}
                      ({fromNow(currentRevision.last_fetched_iso)})
                    </Text>
                  )}
                </>
              ) : (
                'unknown'
              )}
            </InfoCardValue>
          </InfoCard>
          <InfoCard flex="1">
            {commitDetails && (commitDetails.time || commitDetails.user !== 'unknown') ? (
              <>
                <div className="label">Commit Time</div>
                <InfoCardValue>{commitDetails.time || 'unknown'}</InfoCardValue>
                <div className="label">Commit User</div>
                <InfoCardValue>{commitDetails.user || 'unknown'}</InfoCardValue>
                <div className="label">Commit Comment</div>
                <InfoCardValue>{commitDetails.comment}</InfoCardValue>
              </>
            ) : (
              !isLoadingContent &&
              !revisionContentError && (
                <>
                  <div className="label">Commit Details</div>
                  <InfoCardValue>Not available</InfoCardValue>
                </>
              )
            )}
          </InfoCard>
        </Flex>
        <Card flex={1} minHeight="320px" display="flex" flexDirection="column" overflow="hidden">
          {contentBody}
        </Card>
      </FlexColumn>
    );
  }

  renderSearchMatchBadge() {
    const { searchMatchInfo, searchQuery } = this.state;

    if (!searchQuery || searchMatchInfo.totalMatches === 0) {
      return null;
    }

    const { totalMatches, currentMatchIndex } = searchMatchInfo;
    const displayText =
      currentMatchIndex >= 0
        ? `${currentMatchIndex + 1} of ${totalMatches}`
        : `${totalMatches} match${totalMatches !== 1 ? 'es' : ''}`;

    return (
      <Flex height="30px" alignItems="center">
        <SearchMatchBadge>{displayText}</SearchMatchBadge>
      </Flex>
    );
  }

  // Helper to build configs route (avoid repeating raw string in multiple spots)
  buildConfigsRevisionPath = (revisionId) => {
    const { match, standalone } = this.props;

    // In standalone mode, return empty string (no routing)
    if (standalone) {
      return '';
    }

    const base = `/v4/infrastructure/devices/${match.params.deviceName}/configs`;
    return revisionId ? `${base}/${revisionId}` : base;
  };
}
